import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://ai.5vix.com';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
}

export const SEOHead = () => {
  const location = useLocation();

  useEffect(() => {
    const updateSEO = () => {
      const path = location.pathname;
      let schema: SchemaOrgData | null = null;

      // Homepage Schema
      if (path === '/' || path === '') {
        schema = {
          '@context': 'https://schema.org',
          '@type': 'MedicalBusiness',
          name: '5vix',
          description: 'AI-powered medical imaging analysis platform for fracture detection, lung abnormalities, knee osteoarthritis, and scoliosis detection',
          url: SITE_URL,
          logo: 'https://static.readdy.ai/image/c83f6350fa782ce29b612b38d2cee1d4/3839caf1a7724224144a51fe6c60e3dc.svg',
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+20-1060779773',
            contactType: 'Customer Service',
            email: 'info@5vix.com',
            availableLanguage: ['English', 'Arabic']
          },
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'EG'
          },
          medicalSpecialty: [
            'Radiology',
            'Orthopedics',
            'Pulmonology',
            'Dentistry'
          ],
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            reviewCount: '500'
          }
        };

        // Add WebSite schema
        const websiteSchema = {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '5vix',
          url: SITE_URL,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${SITE_URL}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
          }
        };

        // Add SoftwareApplication schema
        const softwareSchema = {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: '5vix AI Medical Imaging',
          applicationCategory: 'HealthApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            ratingCount: '500'
          }
        };

        // Remove existing schemas
        document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

        // Add all schemas
        [schema, websiteSchema, softwareSchema].forEach(s => {
          const script = document.createElement('script');
          script.type = 'application/ld+json';
          script.text = JSON.stringify(s);
          document.head.appendChild(script);
        });
      }

      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `${SITE_URL}${path}`);

      // Update last-modified
      const today = new Date().toISOString().split('T')[0];
      let lastModified = document.querySelector('meta[name="last-modified"]');
      if (!lastModified) {
        lastModified = document.createElement('meta');
        lastModified.setAttribute('name', 'last-modified');
        document.head.appendChild(lastModified);
      }
      lastModified.setAttribute('content', today);

      // Update OG URL
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrl);
      }
      ogUrl.setAttribute('content', `${SITE_URL}${path}`);
    };

    updateSEO();
  }, [location]);

  return null;
};
